import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  setModalConfirmationOpen,
  setModalFormOpen,
} from "redux/actions";
import {
  getAllRaffleAncol,
} from "redux/actions/raffleAncolActions";
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  Badge,
  IconButton,
  theme,
} from "@aksara-ui/react";
import { IconTrash, IconPen, IconBasketAdd} from "@aksara-ui/icons";
import styled from "styled-components";
import moment from "moment";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import { InputSearchbox } from "components/Form/InputSearchbox";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import RaffleAncolFormModal from "./components/RaffleAncolFormModal";
import RaffleAncolConfirmationModal from "./components/RaffleAncolConfirmationModal";
import RaffleAncolViewModal from "./components/RaffleAncolViewModal";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const stripHtmlTags = (html: string): string => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const RaffleAncolContent: React.FC = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ maxWidth: 576 });

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);
  const [selectedRaffleId, setSelectedRaffleId] = useState<string>("");
  const [selectedRaffle, setSelectedRaffle] = useState<any>(null);
  const [isModalViewOpen, setIsModalViewOpen] = useState<boolean>(false);

  const {
    appUrl,
    isModalFormOpen,
    isModalConfirmationOpen,
  } = useSelector((state: any) => state.globalReducer);

  const raffleState = useSelector((state: any) => state.raffleAncolReducer);
  const {
    raffles = [],
    isLoading = false,
    currentPage = 1,
    totalPage = 1,
    limitPage = 10,
    lengthPage = 0,
  } = raffleState || {};

  // ✅ Fungsi untuk refresh list setelah Add/Update/Delete
  const handleRefreshList = () => {
    dispatch(getAllRaffleAncol(currentPage, searchValue, limit) as any);
  };

  const handleSearchChange = (event: any) => {
    const value = event.target.value;
    setSearchValue(value);
    dispatch(getAllRaffleAncol(1, value, limitPage) as any);
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(getAllRaffleAncol(currentPage, "", limitPage) as any);
  };

  const handleOpenFormModal = (raffleId: string = "") => {
    dispatch(setModalFormOpen(true) as any);
    setSelectedRaffleId(raffleId);
  };

  const handleOpenViewModal = (raffle: any) => {
    setSelectedRaffle(raffle);
    setIsModalViewOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsModalViewOpen(false);
    setSelectedRaffle(null);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
    setSelectedRaffleId("");
  };

  const handleOpenConfirmModal = (raffleId: string) => {
    dispatch(setModalConfirmationOpen(true) as any);
    setSelectedRaffleId(raffleId);
  };

  const handleCloseConfirmModal = () => {
    dispatch(setModalConfirmationOpen(false) as any);
    setSelectedRaffleId("");
  };

  useEffect(() => {
    dispatch(getAllRaffleAncol(currentPage, "", limit) as any);
  }, [dispatch]);

  return (
    <Box height={"10==0%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      {/* HEADER */}
      <HeaderWrapper display={"flex"} flexDirection={"column"} style={{ gap: 20 }}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Text scale={[500, 600]} fontWeight={700}>
            Raffle Reward List
          </Text>
          <Button variant="primary" onClick={() => handleOpenFormModal()}>
            Add Raffle Reward
          </Button>
        </Box>

        <InputSearchbox
          autoComplete="off"
          name="textDummy"
          placeholder="Search..."
          size={32}
          onChange={handleSearchChange}
          onClearButton={handleClearSearchChange}
          value={searchValue}
        />
      </HeaderWrapper>

      {/* TABLE */}
      <Card maxHeight={"10%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell>Raffle ID</TableHeadCell>
              <TableHeadCell>Reward Name</TableHeadCell>
              <TableHeadCell>Image</TableHeadCell>
              <TableHeadCell>Description</TableHeadCell>
              <TableHeadCell>Active Period Start</TableHeadCell>
              <TableHeadCell>Active Period End</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell>Action</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton rowsNumber={10} columnsNumber={8} />
            ) : !raffles?.length ? (
              <TableBodyRow>
                <TableBodyCell colSpan={8} textAlign={"center"}>
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              raffles.map((raffle: any) => {
                const now = new Date();
                const start = new Date(raffle.active_periode_start);
                const end = new Date(raffle.active_periode_end);
                const isActive = now >= start && now <= end;

                return (
                  <TableBodyRow key={raffle.id}>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Text fontWeight={600}>{raffle.id}</Text>
                    </TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      {raffle.reward_name}
                    </TableBodyCell>
                    <TableBodyCell>
                      {raffle.img_url && (
                        <img
                          loading="lazy"
                          src={`${appUrl}/storage/${raffle.img_url}`}
                          alt="reward"
                          style={{
                            maxWidth: 100,
                            maxHeight: 100,
                            objectFit: "cover",
                            borderRadius: 6,
                          }}
                        />
                      )}
                    </TableBodyCell>
                    <TableBodyCell>
                      {raffle.description
                        ? stripHtmlTags(raffle.description).substring(0, 50) + "..."
                        : "-"}
                    </TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      {raffle.active_periode_start
                        ? moment(raffle.active_periode_start).format("DD MMM YYYY")
                        : "-"}
                    </TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      {raffle.active_periode_end
                        ? moment(raffle.active_periode_end).format("DD MMM YYYY")
                        : "-"}
                    </TableBodyCell>
                    <TableBodyCell>
                      <Badge size={"md"} variant={isActive ? "success" : "critical"}>
                        {isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      <Box display={"flex"} style={{ gap: 8 }}>
                        <IconButton onClick={() => handleOpenViewModal(raffle)}>
                          <IconBasketAdd fill={theme.colors.blue06} />
                        </IconButton>
                        <IconButton onClick={() => handleOpenFormModal(raffle.id)}>
                          <IconPen fill={theme.colors.blue06} />
                        </IconButton>
                        <IconButton onClick={() => handleOpenConfirmModal(raffle.id)}>
                          <IconTrash fill={theme.colors.red06} />
                        </IconButton>
                      </Box>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* PAGINATION */}
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile && (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        )}
        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: number) =>
            dispatch(getAllRaffleAncol(select, searchValue, limitPage) as any)
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(getAllRaffleAncol(1, searchValue, selectedItem) as any);
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      {/* ✅ MODALS */}
      <RaffleAncolFormModal
        raffleId={selectedRaffleId}
        isOpen={isModalFormOpen}
        onClose={handleCloseFormModal}
        onSuccess={handleRefreshList} // 🔥 refresh otomatis setelah add/update
      />

      <RaffleAncolConfirmationModal
        raffleId={selectedRaffleId}
        isOpen={isModalConfirmationOpen}
        onClose={handleCloseConfirmModal}
        currentPage={currentPage}
        limitPage={limitPage}
      />

      <RaffleAncolViewModal
        isOpen={isModalViewOpen}
        onClose={handleCloseViewModal}
        raffle={selectedRaffle}
        appUrl={appUrl}
      />
    </Box>
  );
};

export default RaffleAncolContent;
